// CRM 2.0 · M12 Template Builder: step 1 Upload (prototype wUpload). After upload the version opens
// with its scan card and blanks.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import cls from "./Templates.module.css";
import { STEPS } from "./templateCommon";
import { getTemplateBuilderSettings, uploadTemplate } from "../../Services/Templates";
import { errorTost, successTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";

// the wizard steps; with onGo the steps in `open` can be clicked
export function StepPills({ current, done = [], open = [], onGo = null }) {
  return (
    <div className={cls.wz}>
      {STEPS.map((name, i) => {
        const n = i + 1;
        const later = false; // every step is built (step 4 Check & preview: Preview with booking)
        const pill = `${cls.wzs} ${n === current ? cls.wzOn : ""} ${done.includes(n) ? cls.wzDone : ""} ${later ? cls.wzLater : ""}`;
        if (onGo && open.includes(n)) {
          return (
            <button key={name} type="button" className={`${pill} ${cls.wzGo}`} aria-current={n === current ? "step" : undefined} onClick={() => onGo(n)}>
              <span>{done.includes(n) ? "✓" : n}</span>{name}
            </button>
          );
        }
        return (
          <div key={name} className={pill} title={later ? "Comes in a later part of M12-F2" : undefined}>
            <span>{done.includes(n) ? "✓" : n}</span>{name}
          </div>
        );
      })}
    </div>
  );
}

function NewTemplate() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [kinds, setKinds] = useState([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [file, setFile] = useState(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTemplateBuilderSettings()
      .then((s) => {
        const active = (s?.values?.documentKinds || []).filter((k) => k.isActive !== false);
        setKinds(active);
        if (active.length) setKind(active[0].key);
      })
      .catch((e) => errorTost(e.message));
  }, []);

  const pick = (f) => {
    if (!f) return;
    if (!/\.docx$/i.test(f.name)) return errorTost("Choose a Word .docx file");
    setFile(f);
    if (!name) setName(f.name.replace(/\.docx$/i, ""));
  };

  async function upload() {
    if (!name.trim()) return errorTost("Template name is required");
    if (!kind) return errorTost("Choose the document type");
    if (!file) return errorTost("Choose the Word file");
    setBusy(true);
    presentLoader();
    try {
      const saved = await uploadTemplate({ file, templateName: name.trim(), templateTypeKey: kind });
      successTost(saved.existed ? "This file was already uploaded: opening that version" : `Uploaded: ${saved.blanks} blanks and ${saved.sections} sections found`);
      navigate(`/home/Templates/${saved.docTemplateId}`);
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
      setBusy(false);
    }
  }

  return (
    <div className={cls.page}>
      <button className={cls.back} onClick={() => navigate("/home/Templates")}>← Templates</button>
      <div className={cls.head}><h1 className={cls.title}>New template</h1></div>
      <StepPills current={1} />

      <div className={cls.panel}>
        <h2 className={cls.panelTitle}>Upload</h2>
        <div className={cls.grid2}>
          <div>
            <label className={cls.lbl} htmlFor="tplName">Template name<sup>*</sup></label>
            <input id="tplName" className={cls.inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ATS – Project name (Tower 1)" />
          </div>
          <div>
            <label className={cls.lbl} htmlFor="tplKind">Document type<sup>*</sup></label>
            <select id="tplKind" className={cls.sel} value={kind} onChange={(e) => setKind(e.target.value)}>
              {kinds.map((k) => <option key={k.key} value={k.key}>{k.name}</option>)}
            </select>
          </div>
        </div>
        <p className={cls.hint} style={{ margin: "1.2rem 0 1.6rem" }}>
          Projects, towers and payment plans are linked to the template after approval (Template Linking).
        </p>
        <div
          className={`${cls.drop} ${over ? cls.dropOn : ""}`}
          onClick={() => fileRef.current && fileRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}
          role="button"
          tabIndex={0}
        >
          {file ? <b>{file.name}</b> : "Drop Legal's Word file here, or click to choose (.docx)"}
          <input ref={fileRef} type="file" accept=".docx" hidden onChange={(e) => pick(e.target.files[0])} />
        </div>
        <p className={cls.hint} style={{ marginTop: "1.2rem" }}>
          The file is stored as it is. The system finds the blanks, sections and checks, and suggests a field for each blank.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "1.2rem" }}>
        <button className={cls.btn} onClick={() => navigate("/home/Templates")}>Cancel</button>
        <button className={`${cls.btn} ${cls.primary}`} disabled={busy} onClick={upload}>{busy ? "Uploading…" : "Upload"}</button>
      </div>
    </div>
  );
}

export default NewTemplate;
